import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoMdClose } from "react-icons/io";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import Payment from "../../Payment/Payment";
import PayAppOfferSelector from "../PayApp/PayAppOfferSelector";
import { formatIstSqlDate } from "../../utils/datetime";
import { continueAfterGateway, parseJson } from "../../utils/paymentResult";

export default function HomeCareBook({ serviceId: serviceIdProp, onClose }) {
  const params = useParams();
  const id = serviceIdProp || params.id;
  const { ApiBaseUrl, setIsLoginShow, paymentStatus, setPaymentStatus, transactionid } = useContext(MyContext);
  const [service, setService] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [quote, setQuote] = useState(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [pvOffer, setPvOffer] = useState(null);
  const [hcPartnerId, setHcPartnerId] = useState(null);
  const [form, setForm] = useState({
    address_text: "", latitude: "17.3850", longitude: "78.4867",
    schedule_date: "", schedule_time: "10:00", notes: "", payment_mode: "online", address_id: ""
  });
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();
  const Userid = secureLocalStorage.getItem("Userid");

  const closeModal = () => {
    document.body.style.overflow = "auto";
    if (onClose) onClose();
    else navigate("/home-care");
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  useEffect(() => {
    if (!id) return;
    setService(null);
    setQuote(null);
    setShowPay(false);
    setPendingBooking(null);
    axios.get(`${ApiBaseUrl}/hc/customer/service/${id}`).then((r) => setService(r.data.service));
    axios.get(`${ApiBaseUrl}/pv/customer/partners`, { params: { partner_type: "hc" } })
      .then((r) => {
        const list = r.data.partners || [];
        if (list.length) setHcPartnerId(list[0].partner_id);
      }).catch(() => {});
    if (Userid) {
      axios.post(`${ApiBaseUrl}/customer/address_list`, { id: Userid }).then((r) => {
        setAddresses(r.data.address_list || r.data.Address_list || []);
      }).catch(() => {});
      navigator.geolocation.getCurrentPosition((pos) => {
        setForm((f) => ({ ...f, latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) }));
      });
    }
  }, [id, ApiBaseUrl, Userid]);

  useEffect(() => {
    if (!form.schedule_date || !id) return;
    axios.post(`${ApiBaseUrl}/hc/customer/quote`, {
      service_id: id, schedule_date: form.schedule_date, schedule_time: form.schedule_time
    }).then((r) => { if (r.data.Result) setQuote(r.data.quote); });
  }, [id, form.schedule_date, form.schedule_time, ApiBaseUrl]);

  useEffect(() => {
    if (paymentStatus !== "PaymentDone" && paymentStatus !== "PaymentFaild") return;
    if (paymentStatus === "PaymentFaild") {
      setPaymentStatus("");
      continueAfterGateway(navigate, { success: false, cancelled: true, transactionId: "0" });
      return;
    }
    setPaymentStatus("");
    continueAfterGateway(navigate, { success: true, transactionId: transactionid || "" });
  }, [paymentStatus, navigate, transactionid, setPaymentStatus]);

  const onAddressPick = (aid) => {
    const a = addresses.find((x) => String(x.id) === String(aid));
    if (!a) return setForm((f) => ({ ...f, address_id: aid }));
    setForm((f) => ({
      ...f,
      address_id: aid,
      address_text: [a.house_no, a.address, a.landmark, a.google_address].filter(Boolean).join(", "),
      latitude: a.latitude || f.latitude,
      longitude: a.longitude || f.longitude
    }));
  };

  const submit = async () => {
    if (!Userid) { setIsLoginShow(true); return; }
    if (!form.address_text || !form.schedule_date || !form.schedule_time) {
      toast.error("Address, date and time are required");
      return;
    }
    const fd = new FormData();
    fd.append("customer_id", Userid);
    fd.append("service_id", id);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
    [...files].forEach((f) => fd.append("files", f));
    try {
      const res = await axios.post(`${ApiBaseUrl}/hc/customer/book`, fd);
      if (!res.data.Result) return toast.error(res.data.message || "Failed");
      const booking = res.data.booking;
      setPendingBooking(booking);
      if (pvOffer?.offer_id) {
        const pv = await axios.post(`${ApiBaseUrl}/pv/customer/txn/create`, {
          customer_id: Userid,
          partner_profile_id: pvOffer.partner.id,
          offer_id: pvOffer.offer_id,
          original_bill: booking.total_amount,
          mode: "booking",
          booking_type: "hc",
          booking_id: booking.id
        });
        if (pv.data.Result) {
          const payAmt = pv.data.txn.payable_amount;
          const payData = {
            txn_id: pv.data.txn.id,
            customer_id: Userid,
            amount: payAmt,
            booking_type: "hc",
            booking_payload: { booking_id: booking.id },
            payment_mode: "online"
          };
          sessionStorage.setItem("page", "5");
          sessionStorage.setItem("PaymentData", JSON.stringify(payData));
          setPendingBooking({ ...booking, total_amount: payAmt, pv_txn_id: pv.data.txn.id });
          toast.info("Complete payment to confirm your booking");
          setShowPay(true);
          return;
        }
      }
      sessionStorage.setItem("page", "4");
      sessionStorage.setItem("PaymentData", JSON.stringify({
        booking_id: booking.id,
        customer_id: Userid,
        amount: booking.total_amount,
        payment_mode: "online"
      }));
      toast.info("Complete payment to confirm your booking");
      setShowPay(true);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={closeModal}
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-[20px] rounded-t-[20px] max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white z-10 flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <p className="text-xs uppercase tracking-wide text-rm-primary font-semibold">Home Care</p>
            <h1 className="text-xl font-bold text-rm-ink">
              {service ? `Book ${service.name}` : "Book service"}
            </h1>
            {service && (
              <p className="text-sm text-gray-600 mt-1">
                ₹{quote?.total_amount || service.base_price} · {service.duration_minutes} mins
                {quote?.rules_applied ? ` · ${quote.rules_applied} pricing rule(s)` : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="shrink-0 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            aria-label="Close"
          >
            <IoMdClose className="text-[22px]" />
          </button>
        </div>

        <div className="px-5 py-5">
          {!service ? (
            <p className="text-center text-gray-500 py-10">Loading form…</p>
          ) : (
            <>
              {addresses.length > 0 && (
                <label className="block mb-3 text-sm font-medium">Saved address
                  <select className="border rounded-lg w-full px-3 py-2 mt-1 font-normal" value={form.address_id} onChange={(e) => onAddressPick(e.target.value)}>
                    <option value="">Select</option>
                    {addresses.map((a) => <option key={a.id} value={a.id}>{a.address_as || a.address || a.house_no}</option>)}
                  </select>
                </label>
              )}
              <label className="block mb-3 text-sm font-medium">Full address
                <textarea className="border rounded-lg w-full px-3 py-2 mt-1 font-normal" rows={3} value={form.address_text} onChange={(e) => setForm({ ...form, address_text: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="text-sm font-medium">Date<input type="date" min={formatIstSqlDate()} className="border rounded-lg w-full px-3 py-2 mt-1 font-normal" value={form.schedule_date} onChange={(e) => setForm({ ...form, schedule_date: e.target.value })} /></label>
                <label className="text-sm font-medium">Time<input type="time" className="border rounded-lg w-full px-3 py-2 mt-1 font-normal" value={form.schedule_time} onChange={(e) => setForm({ ...form, schedule_time: e.target.value })} /></label>
              </div>
              <label className="block mb-3 text-sm font-medium">Notes
                <textarea className="border rounded-lg w-full px-3 py-2 mt-1 font-normal" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>
              <label className="block mb-3 text-sm font-medium">Upload prescription / reports
                <input type="file" multiple className="block mt-1 font-normal" onChange={(e) => setFiles(e.target.files)} />
              </label>
              {hcPartnerId && (
                <PayAppOfferSelector
                  partnerType="hc"
                  partnerId={hcPartnerId}
                  billAmount={quote?.total_amount || service.base_price}
                  customerId={Userid}
                  onChange={setPvOffer}
                />
              )}

              {!showPay ? (
                <button className="w-full bg-rm-primary text-white py-3 rounded-xl font-semibold" onClick={submit}>
                  Continue to pay
                </button>
              ) : (
                <div className="border rounded-xl p-4 bg-gray-50">
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">
                    Booking is unpaid until payment succeeds. Provider dispatch starts after payment.
                  </p>
                  <h3 className="font-semibold mb-3">Pay ₹{pendingBooking?.total_amount}</h3>
                  <Payment
                    Amount={pendingBooking?.total_amount}
                    Data={{
                      ...(parseJson(sessionStorage.getItem("PaymentData"), {}) || {}),
                      booking_id: pendingBooking.id,
                      customer_id: Userid,
                      amount: pendingBooking.total_amount,
                      payment_mode: "online",
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
